'use client';

import Link from 'next/link';
import { CalendarDays, ShoppingCart } from 'lucide-react';
import { usePriceConvert } from '@/lib/PriceContext';
import { useT } from '@/hooks/useT';
import { CountdownClient } from './CountdownClient';
import type { PreorderItem } from '@/lib/api';

const PL_COLOR: Record<string, string> = { XBOXSERIESX: '#6cd45a', XBOXONE: '#6cd45a', PC: '#5cb8ff' };
const PL_LABEL: Record<string, string> = { XBOXSERIESX: 'X|S', XBOXONE: 'ONE', PC: 'PC' };

function PlatformPill({ code }: { code: string }) {
  const label = PL_LABEL[code]; if (!label) return null;
  const color = PL_COLOR[code];
  return (
    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', padding: '3px 9px', borderRadius: 999, border: `1px solid ${color}44`, color, background: `${color}12` }}>
      {label}
    </span>
  );
}

function fmtPrice(price: number | null, cur: string | null) {
  if (!price) return null;
  return new Intl.NumberFormat('uk-UA', { style: 'currency', currency: cur || 'USD', maximumFractionDigits: 2 }).format(price);
}

function fmtDateFull(d: string, lang: string) {
  const locale = lang === 'ru' ? 'ru-RU' : lang === 'en' ? 'en-US' : 'uk-UA';
  return new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}

interface Props {
  item: PreorderItem;
}

export function PreorderHeroClient({ item }: Props) {
  const { convert: fmtConverted, lang } = usePriceConvert();
  const t = useT();

  const title = (lang === 'ru' && item.title_ru) ? item.title_ru
    : (lang === 'uk' && (item as any).title_uk) ? (item as any).title_uk
    : item.title;

  const shortDesc = (lang === 'ru' && item.short_description_ru) ? item.short_description_ru
    : (lang === 'uk' && (item as any).short_description_uk) ? (item as any).short_description_uk
    : item.short_description;

  const price = fmtConverted(item.min_key_usd ?? item.list_price) ?? fmtPrice(item.min_key_usd ?? item.list_price, item.currency_code);

  return (
    <div className="po2-hero">
      <div className="po2-hero-blob po2-hero-blob-1" />
      <div className="po2-hero-blob po2-hero-blob-2" />
      {item.hero && <div className="po2-hero-bg" style={{ backgroundImage: `url(${item.hero})` }} />}
      <div className="po2-hero-vignette" />

      <div className="po2-hero-inner">
        <div className="po2-hero-left">
          <div className="po2-hero-eyebrow">
            <CalendarDays size={12} aria-hidden />
            {t('preorder_next_release')}
          </div>

          <h1 className="po2-hero-title">{title}</h1>

          <p className="po2-hero-pub">
            {[item.publisher_name, item.developer_name].filter(Boolean).join(' · ')}
          </p>

          <div className="po2-hero-platforms">
            {item.platforms.map(p => <PlatformPill key={p} code={p} />)}
          </div>

          <div className="po2-hero-date-row">
            <CalendarDays size={14} color="var(--fg-3)" aria-hidden />
            <span className="po2-hero-date">{fmtDateFull(item.release_at, lang)}</span>
          </div>

          <CountdownClient releaseAt={item.release_at} />

          <div className="po2-hero-actions">
            <Link href={`/product/${item.product_id}`} className="po2-btn-primary">
              <ShoppingCart size={16} aria-hidden />
              {price ? `${t('preorder_preorder')} · ${price}` : t('preorder_preorder')}
            </Link>
          </div>
        </div>

        {item.poster && (
          <div className="po2-hero-right">
            <div className="po2-hero-art-card">
              <img src={item.poster} alt={title} />
              {shortDesc && (
                <div className="po2-hero-art-desc">
                  <p>{shortDesc}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
